<template>
  <div class="api-logs-container">
    <!-- 顶部操作栏 -->
    <div class="page-header">
      <h2>API 调用日志</h2>
      <div class="header-actions">
        <el-button v-if="$canWrite()" type="danger" size="small" icon="el-icon-delete" @click="clearAllLogs">清空所有日志</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="fetchLogs">刷新</el-button>
      </div>
    </div>

    <!-- 筛选条件 - 紧凑布局 -->
    <div class="filter-bar">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="表名">
          <el-input v-model="filters.table_name" placeholder="输入表名" clearable style="width: 150px"></el-input>
        </el-form-item>
        <el-form-item label="操作类型">
          <el-select v-model="filters.operation" placeholder="选择操作" clearable style="width: 120px">
            <el-option label="query" value="query"></el-option>
            <el-option label="get" value="get"></el-option>
            <el-option label="create" value="create"></el-option>
            <el-option label="update" value="update"></el-option>
            <el-option label="delete" value="delete"></el-option>
            <el-option label="count" value="count"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.status" placeholder="选择状态" clearable style="width: 100px">
            <el-option label="成功" value="success"></el-option>
            <el-option label="失败" value="error"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" @click="fetchLogs">查询</el-button>
          <el-button size="small" @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 统计信息 - 内联显示 -->
      <div class="stats-inline">
        <div class="stat-item">
          <span class="stat-label">总调用次数</span>
          <span class="stat-value">{{ statistics.total_calls || 0 }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">成功次数</span>
          <span class="stat-value success">{{ statistics.success_calls || 0 }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">失败次数</span>
          <span class="stat-value error">{{ statistics.error_calls || 0 }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">平均响应</span>
          <span class="stat-value">{{ Math.round(statistics.avg_duration || 0) }}ms</span>
        </div>
      </div>
    </div>

    <!-- 日志列表 -->
    <div class="logs-table-wrapper">
      <el-table
        :data="logs"
        style="width: 100%"
        height="100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column v-if="$canWrite()" type="selection" width="55" fixed></el-table-column>
        <el-table-column prop="table_name" label="表名" min-width="150"></el-table-column>
        <el-table-column prop="operation" label="操作" width="100">
          <template slot-scope="scope">
            <el-tag :type="getOperationTagType(scope.row.operation)" size="small">
              {{ scope.row.operation }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'" size="small">
              {{ scope.row.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="duration" label="耗时" width="100">
          <template slot-scope="scope">
            {{ scope.row.duration }}ms
          </template>
        </el-table-column>
        <el-table-column prop="ip_address" label="IP地址" min-width="140"></el-table-column>
        <el-table-column prop="created_at" label="调用时间" min-width="180"></el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="viewDetail(scope.row)">详情</el-button>
            <el-button type="text" size="small" style="color: #f56c6c" @click="deleteLog(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 底部分页 -->
    <div class="table-footer">
      <div class="footer-left">
        <el-button
          v-if="$canWrite()"
          type="danger"
          size="small"
          :disabled="selectedLogs.length === 0"
          @click="batchDelete"
        >
          批量删除 ({{ selectedLogs.length }})
        </el-button>
      </div>
      
      <el-pagination
        @current-change="handlePageChange"
        :current-page="pagination.page"
        :page-size="pagination.page_size"
        layout="total, prev, pager, next"
        :total="pagination.total"
      ></el-pagination>
    </div>

    <!-- 详情对话框 -->
    <el-dialog title="日志详情" :visible.sync="detailDialogVisible" width="60%">
      <div v-if="currentLog" class="log-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="表名">{{ currentLog.table_name }}</el-descriptions-item>
          <el-descriptions-item label="操作">{{ currentLog.operation }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="currentLog.status === 'success' ? 'success' : 'danger'">
              {{ currentLog.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="耗时">{{ currentLog.duration }}ms</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ currentLog.ip_address }}</el-descriptions-item>
          <el-descriptions-item label="调用时间">{{ currentLog.created_at }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="currentLog.error_message" class="error-message">
          <h4>错误信息：</h4>
          <pre>{{ currentLog.error_message }}</pre>
        </div>

        <div class="request-data">
          <h4>请求数据：</h4>
          <pre>{{ JSON.stringify(currentLog.request_data, null, 2) }}</pre>
        </div>

        <div v-if="currentLog.response_data" class="response-data">
          <h4>响应数据：</h4>
          <pre>{{ JSON.stringify(currentLog.response_data, null, 2) }}</pre>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ApiLogs',
  data() {
    return {
      logs: [],
      selectedLogs: [],
      filters: {
        table_name: '',
        operation: '',
        status: ''
      },
      pagination: {
        page: 1,
        page_size: 20,
        total: 0
      },
      statistics: {},
      detailDialogVisible: false,
      currentLog: null
    };
  },
  computed: {
    projectId() {
      return this.$route.params.id;
    }
  },
  mounted() {
    this.fetchLogs();
    this.fetchStatistics();
  },
  methods: {
    async fetchLogs() {
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.page_size,
          ...this.filters
        };

        const response = await axios.get(`/projects/${this.projectId}/logs`, { params });
        this.logs = response.data.logs;
        this.pagination = response.data.pagination;
      } catch (error) {
        this.$message.error('获取日志失败：' + (error.response?.data?.message || error.message));
      }
    },

    async fetchStatistics() {
      try {
        const response = await axios.get(`/projects/${this.projectId}/logs/statistics`);
        this.statistics = response.data;
      } catch (error) {
        console.error('获取统计信息失败：', error);
      }
    },

    resetFilters() {
      this.filters = {
        table_name: '',
        operation: '',
        status: ''
      };
      this.pagination.page = 1;
      this.fetchLogs();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchLogs();
    },

    handleSelectionChange(selection) {
      this.selectedLogs = selection;
    },

    viewDetail(log) {
      this.currentLog = log;
      this.detailDialogVisible = true;
    },

    async deleteLog(log) {
      try {
        await this.$confirm('确定要删除这条日志吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        await axios.delete(`/projects/${this.projectId}/logs/${log.id}`);
        this.$message.success('删除成功');
        this.fetchLogs();
        this.fetchStatistics();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败：' + (error.response?.data?.message || error.message));
        }
      }
    },

    async batchDelete() {
      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedLogs.length} 条日志吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const ids = this.selectedLogs.map(log => log.id);
        await axios.post(`/projects/${this.projectId}/logs/batch-delete`, { ids });
        this.$message.success('批量删除成功');
        this.fetchLogs();
        this.fetchStatistics();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('批量删除失败：' + (error.response?.data?.message || error.message));
        }
      }
    },

    async clearAllLogs() {
      try {
        await this.$confirm('确定要清空所有日志吗？此操作不可恢复！', '警告', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        await axios.delete(`/projects/${this.projectId}/logs`);
        this.$message.success('清空成功');
        this.fetchLogs();
        this.fetchStatistics();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('清空失败：' + (error.response?.data?.message || error.message));
        }
      }
    },

    getOperationTagType(operation) {
      const types = {
        query: '',
        get: 'success',
        create: 'primary',
        update: 'warning',
        delete: 'danger',
        count: 'info'
      };
      return types[operation] || '';
    }
  }
};
</script>

<style scoped>
.api-logs-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border-bottom: 1px solid #e4e7ed;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: white;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.filter-bar .el-form {
  margin: 0;
}

.filter-bar .el-form-item {
  margin-bottom: 0;
  margin-right: 16px;
}

.stats-inline {
  display: flex;
  gap: 24px;
  align-items: center;
}

.stats-inline .stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stats-inline .stat-label {
  font-size: 13px;
  color: #909399;
}

.stats-inline .stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.stats-inline .stat-value.success {
  color: #67c23a;
}

.stats-inline .stat-value.error {
  color: #f56c6c;
}

.logs-table-wrapper {
  flex: 1;
  margin: 0;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.logs-table-wrapper .el-table {
  flex: 1;
}

.logs-table-wrapper >>> .el-table__body-wrapper {
  overflow-y: auto;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border-top: 1px solid #ebeef5;
  flex-shrink: 0;
}

.footer-left {
  flex-shrink: 0;
}

.log-detail {
  max-height: 600px;
  overflow-y: auto;
}

.log-detail h4 {
  margin-top: 20px;
  margin-bottom: 10px;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
}

.log-detail pre {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}

.error-message pre {
  background-color: #fef0f0;
  color: #f56c6c;
}
</style>
